import { useIntl } from "react-intl";
import { Schema, FieldType } from "../../../toolympus/hooks/useSchema";
import { useLoadedData } from "../../../toolympus/hooks/useLoadedData";
import { useTextFilter } from "../../../toolympus/components/schemed/Filtering/useTextFilter";
import { useEditItem2, useNewItem, withActionOnSaveItem } from "../../../toolympus/api/useNewItem";
import { useItemActionWithConfirmation } from "../../../toolympus/api/useAction";
import { apiFetch } from "../../../toolympus/api/core";

export interface CopTreeNode {
  _id: number;
  title: string;
  type: string;
  description?: any;
  color?: string | null;
  image_url?: string | null;
  sort_order: number;
  path?: string | null;
  is_hidden?: boolean;
  created_at?: string;
  created_by?: string;
  updated_at?: string;
  updated_by?: string;
}

export const CopTreeNodeSchema: Schema = {
  _id: { label: "ID", type: FieldType.number },
  sort_order: { label: "Sort order", type: FieldType.number },
  title: { label: "Title" },
  type: { type: FieldType.dictionarySelect, label: "Type", dictionary: "CopTreeNodeType" },
  path: { label: "Path" },
  description: { label: "Description", type: FieldType.powerdoc },
  color: { label: "Color", type: FieldType.color },
  image_url: { label: "Image URL", type: FieldType.mediafile },
  is_hidden: { label: "Hidden", type: FieldType.bool },
  created_by: { label: "Created by", dictionary: "Users", type: FieldType.dictionarySelect },
  created_at: { label: "Created at", type: FieldType.datetime },
  updated_by: { label: "Updated by", dictionary: "Users", type: FieldType.dictionarySelect },
  updated_at: { label: "Updated at", type: FieldType.datetime },
};

const ApiPath = "/api/cop-decision/tree/node";


export interface MoveTreeNodeItem {
  node: CopTreeNode;
  targetParent: CopTreeNode | null;
}

export const useCopTreeNodes = () => {
  const data = useLoadedData<CopTreeNode[]>(ApiPath, []);
  const filter = useTextFilter<CopTreeNode>(
    c => `${c.title} ${c.type} ${c.path ?? ""} ${c._id}`
  );

  const newNode = useNewItem<Omit<CopTreeNode, "_id">, CopTreeNode>(
    ApiPath,
    {
      title: "",
      type: "",
      path: null,
      sort_order: Math.max(...data.data.map(c => c.sort_order ?? 0), 0) + 1,
      is_hidden: false,
      color: null,
      image_url: null,
      description: null,
    }
  );

  const editNode = useEditItem2<CopTreeNode>({
    getApiPath: c => `${ApiPath}/${c._id}`,
  });

  const { formatMessage } = useIntl();

  const removeNode = useItemActionWithConfirmation<CopTreeNode, {}>(
    item =>
      apiFetch<CopTreeNode>(`${ApiPath}/${item?._id}`, "delete").then(x => {
        data.reload();
        return x;
      }),
    {
      title: formatMessage({ id: "dictionaries.remove_title" }),
      confirmationHint: formatMessage({ id: "dictionaries.remove_hint" }),
    }
  );

  const moveNode = useEditItem2<MoveTreeNodeItem>({
    save: async item => {
      if (!item?.node) return item!;

      const targetPath = item.targetParent
        ? item.targetParent.path
          ? `${item.targetParent.path}.${item.targetParent._id}`
          : `${item.targetParent._id}`
        : null;

      await apiFetch<CopTreeNode>(`${ApiPath}/${item.node._id}/path`, "put", {
        path: targetPath,
      });
      await data.reload();
      return item;
    },
  });

  return {
    ...data,
    data: data.data, //: filter.filterData(data.data),
    filter,
    schema: CopTreeNodeSchema,

    newNode: withActionOnSaveItem(newNode, () => {
      data.reload();
    }),
    editNode: withActionOnSaveItem(editNode, () => {
      data.reload();
    }),
    removeNode,
    moveNode,
  };
};

export type ManageCopTreeNodesData = ReturnType<typeof useCopTreeNodes>;

export const updateCopTreeNode = (id: number, changes: Partial<CopTreeNode>) => {
  return apiFetch<CopTreeNode>(`${ApiPath}/${id}`, "put", changes);
};

